import { Injectable } from "@angular/core";
import { PhiWatermarkService } from "./phi-watermark.service";

@Injectable({
  providedIn: "root",
})
export class PhiSecurityService {
  private isEnabled = false;
  private isVerified = false;
  private restoreTimer: ReturnType<typeof setTimeout> | null = null;
  private eventListeners: Array<{
    element: Window | Document | HTMLElement;
    event: string;
    handler: EventListenerOrEventListenerObject;
    options?: boolean | AddEventListenerOptions;
  }> = [];
  private originalGetDisplayMedia: typeof navigator.mediaDevices.getDisplayMedia | null = null;
  private blurBlankTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private phiWatermarkService: PhiWatermarkService
  ) { }

  // Set PHI verification status — must be true before restrictions can be enabled
  public setVerified(status: boolean): void {
    this.isVerified = status;
  }

  // Enable all PHI security restrictions — called when PHI mode is toggled ON
  enableRestrictions(): void {
    if (!this.isVerified) {
      console.warn("Cannot enable PHI restrictions: PHI verification not completed.");
      return;
    }

    this.isEnabled = true;

    // Core PHI protections
    this.blockPrintScreen();
    this.blockPrintDialog();
    this.handlePrintEvents();
    this.blockScreenshotShortcuts();
    this.blockMacScreenshotShortcuts();
    this.blockContextMenu();
    this.blockDevTools();
    this.blockTextSelection();
    this.blockCopyCut();
    this.blockSaveEvents();
    this.blockScreenCaptureAPI();
    this.addTouchCalloutProtection();

    // iPad/tablet: block touch-based text selection (long-press magnifying glass)
    this.blockTouchSelection();

    // Alt+Tab (Windows) / Cmd+Tab (Mac) / any focus loss — blank after short delay
    // Covers: switching apps, task switcher, clicking outside browser
    // 400ms delay avoids false triggers from address bar clicks or dialog popups
    this.addEventListener(window, "blur", () => {
      if (!this.isVerified || !this.isEnabled) return;
      this.blurBlankTimer = setTimeout(() => {
        this.blankScreen(0);
        this.clearClipboard();
      }, 400);
    }, false);

    // Restore immediately when user returns to the app
    this.addEventListener(window, "focus", () => {
      // Cancel pending blur blank (user returned quickly, e.g. address bar click)
      if (this.blurBlankTimer) {
        clearTimeout(this.blurBlankTimer);
        this.blurBlankTimer = null;
      }
      if (this.isVerified && this.isEnabled) {
        this.restoreScreen();
      }
    });

    // Visibility change — covers iPad Power+Volume screenshot, Slide Over, screen recording
    this.addEventListener(document, "visibilitychange", () => {
      if (!this.isVerified || !this.isEnabled) return;
      if (document.visibilityState === "hidden") {
        this.blankScreen(0);
        this.clearClipboard();
      } else {
        this.restoreScreen();
      }
    });

    // iOS Safari: pagehide/pageshow fire more reliably during app switching
    this.addEventListener(window, "pagehide", () => {
      if (this.isVerified && this.isEnabled) {
        this.blankScreen(0);
        this.clearClipboard();
      }
    });
    this.addEventListener(window, "pageshow", () => {
      if (this.isVerified && this.isEnabled) {
        this.restoreScreen();
      }
    });
  }

  // Disable all PHI security restrictions — called when PHI mode is toggled OFF
  disableRestrictions(): void {
    this.isEnabled = false;
    this.isVerified = false;

    // Clear pending blur blank timer
    if (this.blurBlankTimer) {
      clearTimeout(this.blurBlankTimer);
      this.blurBlankTimer = null;
    }

    // Remove all visual overlays
    this.removePHIWarningOverlay();
    this.removePrintStyle();
    this.restoreScreen();
    this.disableWatermark();

    // Restore Screen Capture API
    this.restoreScreenCaptureAPI();

    // Remove injected CSS
    this.removeTouchCalloutProtection();

    // Remove all event listeners & re-enable text selection
    this.removeAllEventListeners();
    this.enableTextSelection();
  }

  // Check if PHI security restrictions are currently active
  isRestrictionsEnabled(): boolean {
    return this.isEnabled;
  }

  // ==================== SCREENSHOT & PRINT PROTECTION ====================

  // Block PrintScreen key — blanks the screen and clears clipboard
  private blockPrintScreen(): void {
    const handler = (event: KeyboardEvent) => {
      if (event.key === "PrintScreen" || event.keyCode === 44) {
        if (!this.isVerified) return;

        event.preventDefault();
        event.stopPropagation();

        this.blankScreen(2000);
        this.clearClipboard();
        this.showPHIWarningOverlay();
        console.warn("PrintScreen is disabled while PHI mode is active");
      }
    };

    this.addEventListener(document, "keyup", handler, true);
    this.addEventListener(document, "keydown", handler, true);
  }

  // Block screenshot shortcuts: Win+Shift+S, Ctrl+Shift+S, Win+Shift+R
  private blockScreenshotShortcuts(): void {
    const handler = (event: KeyboardEvent) => {
      const isScreenshotShortcut =
        (event.ctrlKey || event.metaKey) &&
        event.shiftKey &&
        (event.key === "S" || event.key === "s");

      const isScreenRecordShortcut =
        (event.ctrlKey || event.metaKey) &&
        event.shiftKey &&
        (event.key === "R" || event.key === "r");

      if (isScreenshotShortcut || isScreenRecordShortcut) {
        if (!this.isVerified) return;

        event.preventDefault();
        event.stopPropagation();

        this.blankScreen(2000);
        this.clearClipboard();
        this.showPHIWarningOverlay();
        console.warn("Screenshot/screen-record shortcut detected — screen blanked");
        return false;
      }
      return true;
    };

    const keyUpHandler = (event: KeyboardEvent) => {
      const isScreenshotShortcut =
        (event.ctrlKey || event.metaKey) &&
        event.shiftKey &&
        (event.key === "S" || event.key === "s" || event.code === "KeyS");

      const isScreenRecordShortcut =
        (event.ctrlKey || event.metaKey) &&
        event.shiftKey &&
        (event.key === "R" || event.key === "r" || event.code === "KeyR");

      if (isScreenshotShortcut || isScreenRecordShortcut) {
        if (!this.isVerified) return;
        this.blankScreen(2000);
        this.clearClipboard();
        this.showPHIWarningOverlay();
      }
    };

    this.addEventListener(document, "keydown", handler, true);
    this.addEventListener(document, "keyup", keyUpHandler, true);
  }

  // Block Mac screenshot shortcuts: Cmd+Shift+3 (full), Cmd+Shift+4 (selection), Cmd+Shift+5 (panel)
  private blockMacScreenshotShortcuts(): void {
    const handler = (event: KeyboardEvent) => {
      if (event.metaKey && event.shiftKey && ["3", "4", "5"].includes(event.key)) {
        if (!this.isVerified) return;

        event.preventDefault();
        event.stopPropagation();

        this.blankScreen(2000);
        this.clearClipboard();
        this.showPHIWarningOverlay();
        console.warn(`Mac screenshot shortcut (Cmd+Shift+${event.key}) blocked`);
        return false;
      }
    };

    this.addEventListener(document, "keydown", handler, true);
  }

  // Block print dialog (Ctrl+P / Cmd+P)
  private blockPrintDialog(): void {
    const handler = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === "p") {
        event.preventDefault();
        event.stopPropagation();
        console.warn("Print function is disabled while PHI mode is active");
      }
    };

    this.addEventListener(document, "keydown", handler, true);
  }

  // Handle browser print events (beforeprint/afterprint) — blanks content during print
  private handlePrintEvents(): void {
    this.addEventListener(window, "beforeprint", () => {
      console.warn("Printing initiated — blanking content for PHI protection");
      this.blankScreen(0);
      this.addPrintStyle();
    });

    this.addEventListener(window, "afterprint", () => {
      this.restoreScreen();
    });

    this.addPrintStyle();
  }

  // ==================== CONTEXT MENU & DEVTOOLS ====================

  // Block right-click context menu
  private blockContextMenu(): void {
    const handler = (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      console.warn("Context menu is disabled while PHI mode is active");
      return false;
    };

    this.addEventListener(document, "contextmenu", handler, true);
  }

  // Block browser DevTools shortcuts (F12, Ctrl+Shift+I/J/C, Ctrl+U)
  private blockDevTools(): void {
    const handler = (event: KeyboardEvent) => {
      // F12
      if (event.key === "F12" || event.keyCode === 123) {
        event.preventDefault();
        event.stopPropagation();
        console.warn("DevTools is disabled while PHI mode is active");
        return false;
      }

      // Ctrl+Shift+I / Cmd+Option+I (Inspector)
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === "I") {
        event.preventDefault();
        event.stopPropagation();
        console.warn("DevTools is disabled while PHI mode is active");
        return false;
      }

      // Ctrl+Shift+J / Cmd+Option+J (Console)
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === "J") {
        event.preventDefault();
        event.stopPropagation();
        console.warn("DevTools is disabled while PHI mode is active");
        return false;
      }

      // Ctrl+Shift+C / Cmd+Option+C (Inspect Element)
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === "C") {
        event.preventDefault();
        event.stopPropagation();
        console.warn("DevTools is disabled while PHI mode is active");
        return false;
      }

      // Ctrl+U / Cmd+U (View Source)
      if ((event.ctrlKey || event.metaKey) && event.key === "u") {
        event.preventDefault();
        event.stopPropagation();
        console.warn("View Source is disabled while PHI mode is active");
        return false;
      }
    };

    this.addEventListener(document, "keydown", handler, true);
  }

  // ==================== TEXT SELECTION & COPY PROTECTION ====================

  // Block text selection on the page
  private blockTextSelection(): void {
    const selectStartHandler = (event: Event) => {
      event.preventDefault();
      return false;
    };

    // Block double-click text selection
    const mouseDownHandler = (event: MouseEvent) => {
      if (event.detail > 1) {
        event.preventDefault();
        return false;
      }
    };

    // Block Ctrl+A / Cmd+A (Select All)
    const selectAllHandler = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && (event.key === "a" || event.key === "A")) {
        event.preventDefault();
        event.stopPropagation();
        console.warn("Select All is disabled while PHI mode is active");
        return false;
      }
    };

    this.addEventListener(document, "selectstart", selectStartHandler, true);
    this.addEventListener(document, "mousedown", mouseDownHandler, true);
    this.addEventListener(document, "keydown", selectAllHandler, true);

    this.addNoSelectStyle();
  }

  // Re-enable text selection — called when PHI mode is turned off
  private enableTextSelection(): void {
    this.removeNoSelectStyle();
  }

  // Block copy and cut
  private blockCopyCut(): void {
    const handler = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && (event.key === "c" || event.key === "x")) {
        event.preventDefault();
        event.stopPropagation();
        console.warn("Copy/Cut is disabled while PHI mode is active");
      }
    };

    this.addEventListener(document, "keydown", handler, true);

    const clipboardHandler = (event: ClipboardEvent) => {
      event.preventDefault();
      event.stopPropagation();
    };

    this.addEventListener(document, "copy", clipboardHandler, true);
    this.addEventListener(document, "cut", clipboardHandler, true);
  }

  // Block touch-based text selection on iPad/tablet (long-press magnifying glass and selection handles)
  private blockTouchSelection(): void {
    let touchTimer: ReturnType<typeof setTimeout> | null = null;

    // Prevent long-press context menu on touch devices
    const touchStartHandler = (_event: TouchEvent) => {
      touchTimer = setTimeout(() => {
        // Clear any selection that started from long-press
        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
        }
      }, 500);
    };

    const touchEndHandler = () => {
      if (touchTimer) {
        clearTimeout(touchTimer);
        touchTimer = null;
      }
    };

    this.addEventListener(document, "touchstart", touchStartHandler, { passive: true } as AddEventListenerOptions);
    this.addEventListener(document, "touchend", touchEndHandler, { passive: true } as AddEventListenerOptions);
    this.addEventListener(document, "touchcancel", touchEndHandler, { passive: true } as AddEventListenerOptions);
  }

  // Block "Save Page As" (Ctrl+S / Cmd+S)
  private blockSaveEvents(): void {
    const handler = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && (event.key === "s" || event.key === "S")) {
        event.preventDefault();
        event.stopPropagation();
        console.warn("Save Page As is disabled while PHI mode is active");
        return false;
      }
    };

    this.addEventListener(document, "keydown", handler, true);
  }

  // ==================== SCREEN CAPTURE API ====================

  // Block getDisplayMedia to prevent screen recording
  private blockScreenCaptureAPI(): void {
    try {
      if (navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia) {
        this.originalGetDisplayMedia = navigator.mediaDevices.getDisplayMedia.bind(navigator.mediaDevices);
        navigator.mediaDevices.getDisplayMedia = () => {
          console.warn("Screen capture API is blocked while PHI mode is active");
          return Promise.reject(new DOMException("Screen capture is disabled in PHI mode", "NotAllowedError"));
        };
      }
    } catch (err) {
      console.error("Failed to override getDisplayMedia:", err);
    }
  }

  // Restore original getDisplayMedia — called when PHI mode is turned off
  private restoreScreenCaptureAPI(): void {
    try {
      if (this.originalGetDisplayMedia && navigator.mediaDevices) {
        navigator.mediaDevices.getDisplayMedia = this.originalGetDisplayMedia;
        this.originalGetDisplayMedia = null;
      }
    } catch (err) {
      console.error("Failed to restore getDisplayMedia:", err);
    }
  }

  // ==================== VISUAL OVERLAYS ====================

  // Show persistent PHI warning overlay at bottom of screen
  public showPHIWarningOverlay(target?: HTMLElement): void {
    const overlayId = "phi-warning-overlay";
    const targetElement = target || (document.fullscreenElement as HTMLElement) || document.body;

    const existing = document.getElementById(overlayId);
    if (existing) {
      if (existing.parentElement !== targetElement) {
        targetElement.appendChild(existing);
      }
      return;
    }

    const overlay = document.createElement("div");
    overlay.id = overlayId;
    overlay.style.cssText = `
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      background: rgba(244, 67, 54, 0.9);
      color: white;
      padding: clamp(4px, 1.5vw, 8px);
      text-align: center;
      z-index: 999998;
      font-family: "Poppins", sans-serif;
      font-size: clamp(10px, 2.5vw, 12px);
      font-weight: bold;
      pointer-events: none;
    `;
    overlay.textContent = "PHI MODE ACTIVE";

    targetElement.appendChild(overlay);
  }

  // Remove the PHI warning overlay from the DOM
  public removePHIWarningOverlay(): void {
    const overlay = document.getElementById("phi-warning-overlay");
    if (overlay) {
      overlay.remove();
    }
  }

  // Blank the screen to prevent screenshot capture
  // @param duration - ms to keep blanked. 0 = persistent until restoreScreen(). Default 2000ms.
  public blankScreen(duration: number = 2000, target?: HTMLElement): void {
    const blankId = "phi-blank-screen";
    const targetElement = target || (document.fullscreenElement as HTMLElement) || document.body;

    if (this.restoreTimer) {
      clearTimeout(this.restoreTimer);
      this.restoreTimer = null;
    }

    let blankOverlay = document.getElementById(blankId);
    if (!blankOverlay) {
      blankOverlay = document.createElement("div");
      blankOverlay.id = blankId;
      blankOverlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: white;
        z-index: 9999999;
        display: flex;
        align-items: center;
        justify-content: center;
        font-family: "Poppins", sans-serif;
        font-size: 24px;
        color: #666;
      `;
      blankOverlay.innerHTML = `
        <div style="text-align: center; padding: 20px;">
          <div style="font-size: clamp(32px, 8vw, 48px); margin-bottom: clamp(10px, 3vw, 20px);">&#128274;</div>
          <div style="font-size: clamp(16px, 4vw, 24px);">PHI Protected Content</div>
          <div style="font-size: clamp(11px, 2.5vw, 14px); margin-top: clamp(6px, 2vw, 10px); color: #999;">Click anywhere to continue</div>
        </div>
      `;
      // Click or touch anywhere on the blank overlay to restore immediately
      blankOverlay.style.cursor = "pointer";
      blankOverlay.addEventListener("click", () => this.restoreScreen());
      blankOverlay.addEventListener("touchend", () => this.restoreScreen());

      targetElement.appendChild(blankOverlay);
      // Force reflow to ensure overlay renders before OS captures screen
      void (blankOverlay as HTMLElement).offsetHeight;
    }

    if (duration > 0) {
      this.restoreTimer = setTimeout(() => {
        this.restoreScreen();
      }, duration);
    }
  }

  // Remove the blank screen overlay and cancel any pending restore timer
  public restoreScreen(): void {
    if (this.restoreTimer) {
      clearTimeout(this.restoreTimer);
      this.restoreTimer = null;
    }

    const blankOverlay = document.getElementById("phi-blank-screen");
    if (blankOverlay) {
      blankOverlay.remove();
    }
  }

  // ==================== CSS HELPERS ====================

  // Inject CSS @media print rule that hides all content and shows a "PRINTING DISABLED" message
  private addPrintStyle(): void {
    const styleId = "phi-print-style";
    if (document.getElementById(styleId)) return;

    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      @media print {
        body { display: none !important; }
        html { background: white !important; }
        * { visibility: hidden !important; }
        body::after {
          content: "PHI PROTECTED - PRINTING DISABLED";
          display: block !important;
          visibility: visible !important;
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-size: 40px;
          color: black;
          border: 5px solid black;
          padding: 20px;
        }
      }
    `;
    document.head.appendChild(style);
  }

  // Remove the injected print-blocking CSS
  private removePrintStyle(): void {
    const style = document.getElementById("phi-print-style");
    if (style) {
      style.remove();
    }
  }

  // Inject CSS that disables text selection across the entire page (user-select: none)
  private addNoSelectStyle(): void {
    const styleId = "phi-no-select-style";
    if (document.getElementById(styleId)) return;

    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      body {
        -webkit-user-select: none !important;
        -moz-user-select: none !important;
        -ms-user-select: none !important;
        user-select: none !important;
      }
    `;
    document.head.appendChild(style);
  }

  // Remove the injected no-select CSS to re-enable text selection
  private removeNoSelectStyle(): void {
    const style = document.getElementById("phi-no-select-style");
    if (style) {
      style.remove();
    }
  }

  // iOS/iPadOS long-press protection
  private addTouchCalloutProtection(): void {
    const styleId = "phi-touch-callout-style";
    if (document.getElementById(styleId)) return;

    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      * {
        -webkit-touch-callout: none !important;
      }
    `;
    document.head.appendChild(style);
  }

  // Remove the injected touch-callout CSS
  private removeTouchCalloutProtection(): void {
    const style = document.getElementById("phi-touch-callout-style");
    if (style) {
      style.remove();
    }
  }

  // Clear clipboard contents — writes a blank space to overwrite any copied PHI data
  // Falls back to legacy execCommand("copy") if Clipboard API is unavailable
  private async clearClipboard(): Promise<void> {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(" ");
      } else {
        throw new Error("Clipboard API unavailable");
      }
    } catch (err) {
      try {
        const textarea = document.createElement("textarea");
        textarea.value = " ";
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      } catch (legacyErr) {
        console.error("Failed to clear clipboard", legacyErr);
      }
    }
  }

  // ==================== EVENT LISTENER MANAGEMENT ====================

  // Register an event listener and track it for cleanup when restrictions are disabled
  private addEventListener(
    element: Window | Document | HTMLElement,
    event: string,
    handler: any,
    options?: boolean | AddEventListenerOptions,
  ): void {
    element.addEventListener(event, handler, options);
    this.eventListeners.push({ element, event, handler, options });
  }

  // Remove all tracked event listeners — called during disableRestrictions() cleanup
  private removeAllEventListeners(): void {
    this.eventListeners.forEach((item) => {
      item.element.removeEventListener(item.event, item.handler, item.options);
    });
    this.eventListeners = [];
  }

  // ==================== PHI WATERMARK METHODS ====================

  // Enable the PHI watermark overlay on the page — delegates to PhiWatermarkService
  enableWatermark(): void {
    this.phiWatermarkService.enableWatermark();
  }

  // Disable the PHI watermark overlay — delegates to PhiWatermarkService
  disableWatermark(): void {
    this.phiWatermarkService.disableWatermark();
  }

  // Toggle the PHI watermark on/off — delegates to PhiWatermarkService
  toggleWatermark(): void {
    this.phiWatermarkService.toggleWatermark();
  }

  // Check if the PHI watermark is currently enabled
  isWatermarkEnabled(): boolean {
    return this.phiWatermarkService.isWatermarkEnabled();
  }
}
